import { useGSAP } from "@gsap/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type PointerEvent,
} from "react";
import { Button } from "react-aria-components";
import gsap from "gsap";
import { Flip } from "gsap/Flip";

import closeIcon from "@/assets/figma/chat-attachments/close.svg";
import documentIcon from "@/assets/figma/chat-file-drop/document-file.svg";
import imageIcon from "@/assets/figma/chat-file-drop/image-file.svg";
import videoIcon from "@/assets/figma/chat-file-drop/video-file.svg";
import { ChatAttachmentPreviewOverlay } from "@/features/chat/ChatAttachmentPreviewOverlay";
import type { ChatAttachment } from "@/features/chat/chatAttachments";

gsap.registerPlugin(Flip, useGSAP);

const COLLAPSED_ROTATIONS = [-5, 2, -3] as const;

interface ChatAttachmentTrayProps {
  attachments: ReadonlyArray<ChatAttachment>;
  onRemove: (attachmentId: string) => void;
  onRequestDownloadUrl: (resourceId: string) => Promise<string>;
  onRequestPreviewUrl: (resourceId: string) => Promise<string>;
  onRetry?: (attachmentId: string) => void;
  previewUrls: Readonly<Record<string, string>>;
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function attachmentTypeIcon(type: ChatAttachment["resourceType"]) {
  if (type === "image") return imageIcon;
  if (type === "video") return videoIcon;
  return documentIcon;
}

function layoutTargets(root: HTMLElement) {
  return Array.from(
    root.querySelectorAll<HTMLElement>("[data-chat-attachment-layout-item]"),
  );
}

export function ChatAttachmentTray({
  attachments,
  onRemove,
  onRequestDownloadUrl,
  onRequestPreviewUrl,
  onRetry,
  previewUrls,
}: ChatAttachmentTrayProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingLayoutRef = useRef<ReturnType<typeof Flip.getState> | null>(
    null,
  );
  const expandedRef = useRef(false);
  const focusWithinRef = useRef(false);
  const pointerWithinRef = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [previewAttachmentId, setPreviewAttachmentId] = useState("");
  const [previewErrorIds, setPreviewErrorIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const attachmentKey = attachments
    .map((attachment) => attachment.attachmentId)
    .join("|");

  const captureLayout = useCallback(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) {
      pendingLayoutRef.current = null;
      return;
    }
    const targets = layoutTargets(root);
    if (targets.length === 0) {
      pendingLayoutRef.current = null;
      return;
    }
    Flip.killFlipsOf(targets, true);
    pendingLayoutRef.current = Flip.getState(targets, { props: "opacity" });
  }, []);

  const updateExpanded = useCallback(
    (nextExpanded: boolean) => {
      if (expandedRef.current === nextExpanded) return;
      captureLayout();
      expandedRef.current = nextExpanded;
      setIsExpanded(nextExpanded);
    },
    [captureLayout],
  );

  useEffect(() => {
    for (const attachment of attachments) {
      if (
        attachment.status !== "ready" ||
        attachment.resourceType !== "image" ||
        !attachment.resourceId ||
        previewUrls[attachment.resourceId] ||
        previewErrorIds.has(attachment.resourceId)
      ) {
        continue;
      }
      const resourceId = attachment.resourceId;
      void onRequestPreviewUrl(resourceId).catch(() => {
        setPreviewErrorIds((current) => new Set(current).add(resourceId));
      });
    }
  }, [
    attachments,
    onRequestPreviewUrl,
    previewErrorIds,
    previewUrls,
  ]);

  const activePreviewAttachmentId = attachments.some(
    (attachment) =>
      attachment.attachmentId === previewAttachmentId &&
      attachment.resourceType === "image" &&
      attachment.status === "ready",
  )
    ? previewAttachmentId
    : "";

  useGSAP(
    () => {
      const root = rootRef.current;
      const previousLayout = pendingLayoutRef.current;
      pendingLayoutRef.current = null;
      if (!root) return;
      const targets = layoutTargets(root);
      const cards = Array.from(
        root.querySelectorAll<HTMLElement>("[data-chat-attachment-card]"),
      );
      const controls = Array.from(
        root.querySelectorAll<HTMLElement>("[data-chat-attachment-control]"),
      );
      Flip.killFlipsOf(targets, true);
      gsap.killTweensOf([...cards, ...controls]);

      if (prefersReducedMotion()) {
        root.dataset.attachmentLayoutAnimating = "false";
        gsap.set([...targets, ...cards, ...controls], {
          clearProps: "all",
        });
        return;
      }

      root.dataset.attachmentLayoutAnimating = previousLayout
        ? "true"
        : "false";
      if (previousLayout) {
        Flip.from(previousLayout, {
          duration: 0.34,
          ease: "power3.inOut",
          nested: true,
          prune: true,
          scale: false,
          onComplete: () => {
            root.dataset.attachmentLayoutAnimating = "false";
            gsap.set(targets, { clearProps: "transform,width,height" });
          },
          onInterrupt: () => {
            root.dataset.attachmentLayoutAnimating = "false";
          },
        });
      }

      cards.forEach((card) => {
        const collapsedRotation = Number(
          card.dataset.collapsedRotation ?? "0",
        );
        gsap.to(card, {
          duration: 0.34,
          ease: "power3.inOut",
          rotation: isExpanded ? 0 : collapsedRotation,
          onComplete: () => {
            gsap.set(card, { clearProps: "transform" });
          },
        });
      });
      gsap.to(controls, {
        autoAlpha: isExpanded ? 1 : 0,
        duration: isExpanded ? 0.18 : 0.12,
        ease: "power2.out",
        scale: isExpanded ? 1 : 0.82,
        stagger: isExpanded ? 0.018 : 0,
        onComplete: () => {
          gsap.set(controls, { clearProps: "opacity,visibility,transform" });
        },
      });
    },
    { dependencies: [attachmentKey, isExpanded], scope: rootRef },
  );

  const handlePointerEnter = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;
    pointerWithinRef.current = true;
    updateExpanded(true);
  };

  const handlePointerLeave = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") return;
    pointerWithinRef.current = false;
    if (!focusWithinRef.current) updateExpanded(false);
  };

  const handleFocus = () => {
    focusWithinRef.current = true;
    updateExpanded(true);
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget && rootRef.current?.contains(event.relatedTarget)) {
      return;
    }
    focusWithinRef.current = false;
    if (!pointerWithinRef.current) updateExpanded(false);
  };

  const removeAttachment = (attachmentId: string) => {
    captureLayout();
    onRemove(attachmentId);
  };

  return (
    <>
      <div
        aria-label="已引用云盘文件"
        className="merry-chat-attachment-tray"
        data-expanded={isExpanded ? "true" : "false"}
        data-figma-node-id={isExpanded ? "571:61310" : "571:61053"}
        data-testid="chat-attachment-tray"
        onBlurCapture={handleBlur}
        onFocusCapture={handleFocus}
        onKeyDown={(event) => {
          if (event.key === "Escape") updateExpanded(false);
        }}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        ref={rootRef}
        role="group"
      >
        <div className="merry-chat-attachment-tray__viewport">
          <div className="merry-chat-attachment-tray__list">
            {attachments.map((attachment, index) => {
              const isStackVisible = isExpanded || index < 3;
              const previewUrl = attachment.resourceId
                ? previewUrls[attachment.resourceId]
                : "";
              const canPreview =
                attachment.status === "ready" &&
                attachment.resourceType === "image" &&
                Boolean(attachment.resourceId);
              const collapsedRotation =
                COLLAPSED_ROTATIONS[Math.min(index, 2)] ?? -3;
              const itemStyle = {
                "--attachment-index": index,
              } as CSSProperties;
              return (
                <div
                  className="merry-chat-attachment-tray__item"
                  data-chat-attachment-layout-item=""
                  data-resource-id={attachment.resourceId ?? undefined}
                  data-stack-visible={isStackVisible ? "true" : "false"}
                  data-upload-status={attachment.status}
                  key={attachment.attachmentId}
                  style={itemStyle}
                >
                  <div
                    className="merry-chat-attachment-tray__card"
                    data-chat-attachment-card=""
                    data-collapsed-rotation={collapsedRotation}
                    data-resource-type={attachment.resourceType}
                  >
                    <Button
                      aria-label={
                        canPreview
                          ? `预览 ${attachment.displayName}`
                          : isExpanded
                            ? `附件 ${attachment.displayName}`
                            : `展开附件 ${attachment.displayName}`
                      }
                      className="merry-chat-attachment-tray__surface"
                      isDisabled={!isStackVisible}
                      onPress={() => {
                        if (!isExpanded) {
                          updateExpanded(true);
                          return;
                        }
                        if (canPreview) {
                          setPreviewAttachmentId(attachment.attachmentId);
                        }
                      }}
                    >
                      {attachment.resourceType === "image" && previewUrl ? (
                        <>
                          <img
                            alt=""
                            aria-hidden="true"
                            className="merry-chat-attachment-tray__media"
                            draggable="false"
                            src={previewUrl}
                          />
                          <span className="sr-only">
                            {attachment.displayName}
                          </span>
                        </>
                      ) : (
                        <span className="merry-chat-attachment-tray__file">
                          <img
                            alt=""
                            aria-hidden="true"
                            src={attachmentTypeIcon(attachment.resourceType)}
                          />
                          <span title={attachment.displayName}>
                            {attachment.displayName}
                          </span>
                        </span>
                      )}
                      {attachment.status !== "ready" ? (
                        <span
                          className="merry-chat-attachment-tray__status"
                          role="status"
                        >
                          {attachment.status === "uploading"
                            ? "上传中"
                            : attachment.errorMessage || "上传失败"}
                        </span>
                      ) : null}
                    </Button>
                    {attachment.status === "failed" && onRetry ? (
                      <Button
                        aria-label={`重试 ${attachment.displayName}`}
                        className="merry-chat-attachment-tray__retry"
                        isDisabled={!isStackVisible}
                        onPress={() => onRetry(attachment.attachmentId)}
                      >
                        重试
                      </Button>
                    ) : null}
                  </div>
                  <Button
                    aria-label={`移除 ${attachment.displayName}`}
                    className="merry-chat-attachment-tray__remove"
                    data-chat-attachment-control=""
                    isDisabled={!isExpanded}
                    onPress={() => removeAttachment(attachment.attachmentId)}
                  >
                    <img alt="" aria-hidden="true" src={closeIcon} />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {activePreviewAttachmentId ? (
        <ChatAttachmentPreviewOverlay
          attachments={attachments}
          onClose={() => setPreviewAttachmentId("")}
          onRemove={removeAttachment}
          onRequestDownloadUrl={onRequestDownloadUrl}
          onRequestPreviewUrl={onRequestPreviewUrl}
          onSelect={setPreviewAttachmentId}
          previewUrls={previewUrls}
          selectedAttachmentId={activePreviewAttachmentId}
        />
      ) : null}
    </>
  );
}
